

import { useState, useEffect, useRef } from 'react'
import { apiFetch } from '@/lib/api'
import { useShow } from '@/contexts/ShowContext'
import { useEpisode } from '@/contexts/EpisodeContext'
import { UserRole } from '@/contexts/UserContext'
import { useParams } from 'react-router-dom'
import { LuLoaderCircle } from 'react-icons/lu'
import Button from '@/components/ui/Button'
import CommentMentionsInput from '@/components/comments/CommentMentionsInput'
import CommentGroup from '@/components/comments/CommentGroup'

export default function StoryboardComments({ storyboard, selectedElementIndex, onSelectElement, highlightCommentId }) {
    const { show } = useShow()
    const { episode } = useEpisode()
    const { segmentId } = useParams()
    const [comments, setComments] = useState([])
    const [commentText, setCommentText] = useState('')
    const [loading, setLoading] = useState(true)
    const commentInputRef = useRef(null)

    const elementIds = storyboard?.elements?.map(e => e._id) || []
    const selectedElement = storyboard?.elements?.[selectedElementIndex] || null

    // Build element name map for labels
    const elementNameMap = {}
    storyboard?.elements?.forEach((el, idx) => {
        elementNameMap[el._id] = el.name || `Canvas ${idx + 1}`
    })

    // Build element index map
    const elementIndexMap = {}
    storyboard?.elements?.forEach((el, idx) => {
        elementIndexMap[el._id] = idx
    })

    const fetchComments = async () => {
        if (!show?._id || !elementIds.length) {
            setComments([])
            setLoading(false)
            return
        }
        try {
            const response = await apiFetch(`/api/shows/${show._id}/comments?uid=${elementIds.join(',')}`)
            if (!response.ok) throw new Error('Failed to fetch comments')
            const data = await response.json()
            setComments(data)
        } catch (error) {
            console.error('Error fetching comments:', error)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        setLoading(true)
        fetchComments()
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storyboard?._id, show?._id])

    const handleCommentSubmit = async () => {
        if (!selectedElement || !commentText.trim()) return
        try {
            const response = await apiFetch(`/api/shows/${show._id}/comments`, {
                method: 'POST',
                body: JSON.stringify({
                    uid: selectedElement._id,
                    text: commentText,
                    element_type: 'canvas',
                    episode: episode?._id,
                    segment: segmentId
                })
            })
            if (!response.ok) throw new Error('Failed to submit comment')
            const data = await response.json()
            setComments(prev => [...prev, data])
            setCommentText('')
        } catch (error) {
            console.error('Error submitting comment:', error)
        }
    }

    const handleCommentKeyDown = (e) => {
        if (e.key === 'Enter' && e.ctrlKey) {
            e.preventDefault()
            handleCommentSubmit()
        } else if (e.key === 'Escape') {
            setCommentText('')
        }
    }

    const handleCommentClick = (comment) => {
        const elementId = typeof comment.element === 'object' ? comment.element.toString() : comment.element
        const idx = elementIndexMap[elementId]
        if (idx !== undefined) {
            onSelectElement?.(idx)
        }
    }

    return (
        <div className='w-72 flex flex-col border-l border-l-secondary/60 bg-background/50 shrink-0'>
            <div className='flex items-center justify-between px-3 h-10 border-b border-b-secondary/60'>
                <span className='text-xs font-medium text-secondary-foreground'>Comments</span>
                {comments.length > 0 && <span className='text-[10px] text-secondary-foreground/50 bg-secondary rounded-full px-1.5 py-0.5 min-w-[20px] text-center'>{comments.length}</span>}
            </div>

            <div className='flex-1 overflow-y-auto px-2 py-2'>
                {loading ? (
                    <div className='flex items-center justify-center py-8'>
                        <LuLoaderCircle className='w-4 h-4 animate-spin text-secondary-foreground/50' />
                    </div>
                ) : comments.length === 0 ? (
                    <div className='flex items-center justify-center py-8'>
                        <span className='text-xs text-secondary-foreground/40'>No comments yet</span>
                    </div>
                ) : (
                    <div className='flex flex-col gap-2'>
                        {comments.map((comment) => (
                            <CommentGroup
                                key={comment._id}
                                data={comment}
                                onDelete={fetchComments}
                                onClick={() => handleCommentClick(comment)}
                                highlightCommentId={highlightCommentId}
                                elementName={elementNameMap[typeof comment.element === 'object' ? comment.element.toString() : comment.element]}
                            />
                        ))}
                    </div>
                )}
            </div>

            <UserRole role='commentator'>
                <div className='p-2'>
                    {selectedElement ? (
                        <div className='flex flex-col gap-1'>
                            <span className='text-[10px] text-secondary-foreground/50 px-1'>
                                Commenting on: <span className='text-primary/70'>{selectedElement.name || 'Untitled'}</span>
                            </span>
                            <CommentMentionsInput
                                value={commentText}
                                onChange={setCommentText}
                                onKeyDown={handleCommentKeyDown}
                                placeholder='Add a comment...'
                                inputRef={commentInputRef}
                            />
                            <div className='flex items-center justify-end gap-1'>
                                <Button type='confirm' size='xs' onClick={handleCommentSubmit} disabled={!commentText.trim()}>
                                    Comment
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div className='text-[10px] text-secondary-foreground/40 text-center py-2'>
                            Select a canvas to add comments
                        </div>
                    )}
                </div>
            </UserRole>
        </div>
    )
}
